import React, { Component } from "react";
import { Loader, Message } from "semantic-ui-react";
import api from "../../../../../api";
import TestCallVariablesModal from "./TestCallVariablesModal";
import BotbuilderLoadingOverlay from "./BotbuilderLoadingOverlay";

/**
 * Voice_Turbo (inbound): Talk → start call (no test-variable modal).
 * OUTBOUND: optional Test Call Setup when {{variables}} exist; values saved via API and merged into update-variables.
 */
class VapiAssistant extends Component {
  constructor(props) {
    super(props);
    this.state = {
      status: "Not connected",
      transcript: [],
      inCall: false,
      isSpeaking: false,
      showPopup: false,
      waitingForFirstResponse: false,
      hasUserSpoken: false,
      error: false,
      squadId: "",
      testCallVariablesMap: {},
      showTestVariablesModal: false,
      testVariablesModalMode: "setup",
      testVariablesModalNames: [],
      testVariablesModalInitial: {},
      testVariablesValidationError: "",
      editTestVariablesLoading: false,
      editTestVariablesError: "",
      talkAssistantLoading: false,
    };
    this.transcriptRef = React.createRef();
    this.vapi = null;
    this.isStartingCall = false;
    this.isCallActive = false;
    this._pendingVapiTestVars = {};
  }

  componentDidMount() {
    if (window.Vapi) {
      this.vapi = new window.Vapi(this.props.apiKey);

      this.vapi.on("call-start", () => {
        this.setState({ talkAssistantLoading: false });
        this.isCallActive = true;
        const outbound = !!this.props.requireHumanFirst;
        if (outbound) {
          this.setState({
            status: "✅ Connected — speak first to start",
            inCall: true,
            waitingForFirstResponse: true,
            hasUserSpoken: false,
          });
        } else {
          this.setState({ status: "✅ Connected", inCall: true, waitingForFirstResponse: false });
        }
        this.props.disableScrenInCall("pointerNone");

        const baseVars = {
          CURRENTDATETIME: this.formatDateTime(new Date()),
          CURRENTDATE: new Date().toLocaleDateString(),
          CURRENTTIME: new Date().toLocaleTimeString(),
        };
        const testVars = { ...(this._pendingVapiTestVars || {}) };
        this.vapi.send({
          type: "update-variables",
          variables: outbound
            ? { ...baseVars, HUMAN_SPEAKS_FIRST: true, ...testVars }
            : { ...baseVars, ...testVars },
        });
      });

      this.vapi.on("call-end", (reason) => {
        this.isStartingCall = false;
        this.isCallActive = false;
        this.setState((prev) => ({
          transcript: [
            ...prev.transcript,
            { role: "system", text: `📞 Call ended: ${reason || "Disconnected"}` },
          ],
          status: "❌ Disconnected",
          inCall: false,
          isSpeaking: false,
          /** Always hide floating panel so 🎤 Talk To Assistant is available again (replay / next call). */
          showPopup: false,
          error: false,
        }));
        this.props.disableScrenInCall("");
      });

      this.vapi.on("speech-start", () => {
        const outbound = !!this.props.requireHumanFirst;
        if (outbound && !this.state.hasUserSpoken) {
          this.setState({
            status: "Waiting for you to speak first",
            isSpeaking: false,
            waitingForFirstResponse: true,
          });
          return;
        }
        this.setState({ status: "Assistant speaking…", isSpeaking: true, waitingForFirstResponse: false });
      });
      this.vapi.on("speech-end", () => this.setState({ status: "Listening…", isSpeaking: false }));
      this.vapi.on("message", (message) => {
        if (message.type === "transcript") {
          const outbound = !!this.props.requireHumanFirst;
          if (outbound && message.role === "user" && message.transcript && String(message.transcript).trim() !== "") {
            this.setState({
              hasUserSpoken: true,
              waitingForFirstResponse: false,
              status: "Listening…",
            });
          } else if (this.state.waitingForFirstResponse && !outbound) {
            this.setState({ waitingForFirstResponse: false });
          }
          const isFinal = message.transcriptType === "final";

          this.setState((prev) => {
            const transcript = [...prev.transcript];
            const last = transcript[transcript.length - 1];

            if (!isFinal) {
              if (last && last.role === message.role && last.transcriptType === "interim") {
                transcript[transcript.length - 1] = {
                  role: message.role,
                  text: message.transcript,
                  transcriptType: "interim",
                };
              } else {
                transcript.push({
                  role: message.role,
                  text: message.transcript,
                  transcriptType: "interim",
                });
              }
            } else {
              if (last && last.role === message.role && last.transcriptType === "interim") {
                transcript[transcript.length - 1] = {
                  role: message.role,
                  text: message.transcript,
                  transcriptType: "final",
                };
              } else if (!last || last.text !== message.transcript) {
                transcript.push({
                  role: message.role,
                  text: message.transcript,
                  transcriptType: "final",
                });
              }
            }

            return { transcript };
          }, () => {
            if (this.transcriptRef.current) {
              this.transcriptRef.current.scrollTop = this.transcriptRef.current.scrollHeight;
            }
          });
        }
      });
      this.vapi.on("error", (err) => {
        console.error("Vapi error:", err);
        this.setState((prev) => ({
          transcript: [
            ...prev.transcript,
            { role: "system", text: `⚠️ Error: ${err.errorMsg || err.error.message || err}` },
          ],
          showPopup: true,
          error: true,
          waitingForFirstResponse: false,
          talkAssistantLoading: false,
        }));
      });
    } else {
      console.error("⚠️ Vapi SDK not loaded — be sure to include the CDN script in index.html.");
    }
  }

  formatDateTime = (date) => {
    const days = [
      "Sunday", "Monday", "Tuesday", "Wednesday",
      "Thursday", "Friday", "Saturday",
    ];
    const months = [
      "January", "February", "March", "April",
      "May", "June", "July", "August",
      "September", "October", "November", "December",
    ];

    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    function getOrdinal(n) {
      if (n > 3 && n < 21) return n + "th";
      switch (n % 10) {
        case 1: return n + "st";
        case 2: return n + "nd";
        case 3: return n + "rd";
        default: return n + "th";
      }
    }

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${dayName}, ${monthName} ${getOrdinal(day)}, ${year} ${hours}:${minutes} ${ampm}`;
  };

  /**
   * OUTBOUND: welcome branch only (index 0), merged with in-memory editor when editing welcome.
   */
  getOutboundRelevantBranchList = () => {
    const list = this.props.branchList || [];
    if (!list.length) return [];
    const intro = list[0];
    if (!intro) return [];

    const introId =
      intro.branch_id != null
        ? String(intro.branch_id)
        : intro.details && intro.details.branch_id != null
          ? String(intro.details.branch_id)
          : "";
    const editing = this.props.currentBranchDetails;
    const editingIsObject =
      editing &&
      typeof editing === "object" &&
      !Array.isArray(editing);
    const editingId =
      editingIsObject &&
      (
        (editing.branch_id != null && String(editing.branch_id).trim() !== "") ||
        (editing.details && editing.details.branch_id != null && String(editing.details.branch_id).trim() !== "")
      )
        ? String(editing.branch_id != null ? editing.branch_id : editing.details.branch_id).trim()
        : "";

    if (introId && editingIsObject && editingId === introId) {
      const eq = editing.branch_questions;
      const iq = intro.branch_questions;
      const eb = editing.branch_buttons;
      const ib = intro.branch_buttons;
      /**
       * Prefer non-empty question list: empty `[]` from editor state must not wipe
       * saved intro text (where {{variables}} often still live until save).
       */
      const branch_questions =
        Array.isArray(eq) && eq.length > 0 ? eq : Array.isArray(iq) ? iq : eq || [];
      const branch_buttons =
        Array.isArray(eb) && eb.length > 0 ? eb : Array.isArray(ib) ? ib : eb || [];

      return [
        {
          ...intro,
          ...editing,
          branch_questions,
          branch_buttons,
          details: {
            ...(intro.details || {}),
            ...(editing.details || {}),
          },
        },
      ];
    }
    return [intro];
  };

  pickTestVariableValues = (saved, names) => {
    const o = {};
    (names || []).forEach((n) => {
      if (saved && saved[n] != null && String(saved[n]).trim() !== "") {
        o[n] = String(saved[n]);
      }
    });
    return o;
  };

  handleTalkButtonClick = () => {
    this.setState({ talkAssistantLoading: true });
    const outbound = !!this.props.requireHumanFirst;
    if (!outbound) {
      this.setState({ testCallVariablesMap: {} }, () => {
        this._pendingVapiTestVars = {};
        this.executeStartCall();
      });
      return;
    }

    const workspaceId = this.props.workspaceId;
    if (workspaceId === undefined || workspaceId === null || workspaceId === "") {
      this.setState({ testCallVariablesMap: {} }, () => {
        this._pendingVapiTestVars = {};
        this.executeStartCall();
      });
      return;
    }

    api.botbuilder
      .getWorkspaceTestVariables({ workspace_id: workspaceId })
      .then((res) => {
        const names = (res && res.data && res.data.variable_names) || [];
        const saved = (res && res.data && res.data.variables) || {};
        if (names.length === 0) {
          this.setState({ testCallVariablesMap: {} }, () => {
            this._pendingVapiTestVars = {};
            this.executeStartCall();
          });
          return;
        }
        const complete = names.every((n) => String(saved[n] || "").trim() !== "");
        if (complete) {
          const picked = this.pickTestVariableValues(saved, names);
          this.setState(
            { testCallVariablesMap: picked, testVariablesValidationError: "" },
            () => {
              this._pendingVapiTestVars = { ...picked };
              this.executeStartCall();
            }
          );
          return;
        }
        this.setState({
          showTestVariablesModal: true,
          testVariablesModalMode: "setup",
          testVariablesModalNames: names,
          testVariablesModalInitial: { ...saved },
          testVariablesValidationError: "",
          talkAssistantLoading: false,
        });
      })
      .catch(() => {
        this.setState({ talkAssistantLoading: false });
      });
  };

  handleTestVariablesSave = (values) => {
    const names = this.state.testVariablesModalNames || [];
    const workspaceId = this.props.workspaceId;
    const mode = this.state.testVariablesModalMode;
    if (names.length === 0) {
      this.setState({ showTestVariablesModal: false, testVariablesValidationError: "" });
      return;
    }
    const missing = names.some((n) => !String(values[n] || "").trim());
    if (missing) {
      this.setState({
        testVariablesValidationError: "Some variables are missing values",
      });
      return;
    }
    this.setState({ testVariablesValidationError: "" });
    api.botbuilder
      .saveWorkspaceTestVariables({ workspace_id: workspaceId, variables: values })
      .then((res) => {
        if (res && res.status === false) {
          this.setState({
            testVariablesValidationError: "Could not save test variables. Please try again.",
          });
          return;
        }
        const picked = {};
        names.forEach((n) => {
          picked[n] = String(values[n] || "");
        });
        this.setState(
          {
            showTestVariablesModal: false,
            testVariablesValidationError: "",
            testCallVariablesMap: picked,
          },
          () => {
            this._pendingVapiTestVars = { ...picked };
            if (mode === "setup") {
              this.executeStartCall();
            }
          }
        );
      });
  };

  handleTestVariablesCancel = () => {
    this.setState({
      showTestVariablesModal: false,
      testVariablesValidationError: "",
    });
  };

  handleEditVariablesClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!this.props.requireHumanFirst) return;
    const workspaceId = this.props.workspaceId;
    if (workspaceId === undefined || workspaceId === null || workspaceId === "") {
      return;
    }
    this.setState({ editTestVariablesLoading: true, editTestVariablesError: "" });
    api.botbuilder
      .getWorkspaceTestVariables({ workspace_id: workspaceId })
      .then((res) => {
        if (!res || res.status === false) {
          this.setState({
            editTestVariablesError:
              "Could not load test variables. Please try again.",
          });
          return;
        }
        const names = (res && res.data && res.data.variable_names) || [];
        const saved = (res && res.data && res.data.variables) || {};
        this.setState({
          showTestVariablesModal: true,
          testVariablesModalMode: "edit",
          testVariablesModalNames: names,
          testVariablesModalInitial: { ...saved },
          testVariablesValidationError: "",
          editTestVariablesError: "",
        });
      })
      .finally(() => {
        this.setState({ editTestVariablesLoading: false });
      });
  };

  executeStartCall = () => {
    const { userId } = this.props;
    if (!this.vapi) {
      this.setState({ talkAssistantLoading: false });
      return;
    }

    this._pendingVapiTestVars = { ...(this.state.testCallVariablesMap || {}) };
    this.isStartingCall = true;
    this.isCallActive = false;
    this.setState({ talkAssistantLoading: true });
    const outbound = !!this.props.requireHumanFirst;
    if (outbound) {
      this.setState({
        transcript: [],
        showPopup: true,
        waitingForFirstResponse: true,
        hasUserSpoken: false,
        status: "Connecting — speak first when connected",
      });
    } else {
      this.setState({ transcript: [], showPopup: true, waitingForFirstResponse: true });
    }

    const turboConfigParams = outbound
      ? {
          userId,
          workspace_id: this.props.workspaceId,
          bot_type: this.props.botType || "OUTBOUND",
          test_mode: this.props.testMode || 1,
        }
      : userId;

    api.botbuilder
      .getTurBoConfig(turboConfigParams)
      .then((data) => {
        try {
          if (!this.isStartingCall) {
            console.log("❌ Call cancelled before API resolved");
            this.setState({ talkAssistantLoading: false });
            return;
          }

          if (data.status) {
            const payload = data.data || {};
            const serverVarValues =
              payload.assistantOverrides &&
              payload.assistantOverrides.variableValues &&
              typeof payload.assistantOverrides.variableValues === "object"
                ? payload.assistantOverrides.variableValues
                : {};
            const testMap = this.state.testCallVariablesMap || {};
            this._pendingVapiTestVars = { ...serverVarValues, ...testMap };
            try {
              if (data.squadId) {
                this.setState({ squadId: data.squadId });
                this.vapi.start(null, null, data.squadId);
              } else {
                this.vapi.start(
                  payload.assistant,
                  payload.assistantOverrides || undefined
                );
              }
            } catch (startErr) {
              console.log(startErr);
              this.isStartingCall = false;
              this.setState({
                talkAssistantLoading: false,
                waitingForFirstResponse: false,
                showPopup: false,
                error: true,
              });
            }
          } else {
            this.isStartingCall = false;
            this.setState((prev) => ({
              transcript: [
                ...prev.transcript,
                {
                  role: "system",
                  text: `⚠️ Error: ${data.message || data.error || "Unable to start call"}`,
                },
              ],
            waitingForFirstResponse: false,
            showPopup: false,
            error: true,
            talkAssistantLoading: false,
            }));
          }
        } catch (err) {
          console.log(err);
          this.isStartingCall = false;
          this.setState({ talkAssistantLoading: false });
        }
      })
      .catch((err) => {
        console.log(err);
        this.isStartingCall = false;
        this.setState((prev) => ({
          transcript: [
            ...prev.transcript,
            {
              role: "system",
              text: "⚠️ Error: Unable to start call. Please try again.",
            },
          ],
          waitingForFirstResponse: false,
          showPopup: false,
          error: true,
          talkAssistantLoading: false,
        }));
      });
  };

  endCall = () => {
    if (this.vapi) {
      this.isStartingCall = false;
      this.isCallActive = false;
      if (this.state.squadId && this.state.squadId !== "") {
        api.botbuilder.deleteSquad(this.state.squadId).then((data) => {
          try {
            this.setState({ squadId: "" });
          } catch (err) {
            console.log(err);
          }
        });
      }

      this.setState({
        callEnded: true,
        transcript: [],
        showPopup: false,
        error: false,
        talkAssistantLoading: false,
      });
      this.vapi.stop();
    }
  };

  render() {
    const { transcript, inCall, showPopup } = this.state;
    const outbound = !!this.props.requireHumanFirst;
    const defaultLabel = "🎤 Talk To Assistant";
    const showOutboundEditLink =
      outbound &&
      this.props.workspaceId !== undefined &&
      this.props.workspaceId !== null &&
      String(this.props.workspaceId).trim() !== "";

    const assistantMissing =
      outbound &&
      (!this.props.assistantId || String(this.props.assistantId).trim() === "");

    return (
      <>
        <BotbuilderLoadingOverlay
          active={
            this.state.editTestVariablesLoading || this.state.talkAssistantLoading
          }
        />
        <TestCallVariablesModal
          open={this.state.showTestVariablesModal}
          mode={this.state.testVariablesModalMode}
          variableNames={this.state.testVariablesModalNames}
          initialValues={this.state.testVariablesModalInitial}
          onSave={this.handleTestVariablesSave}
          onCancel={this.handleTestVariablesCancel}
          validationError={this.state.testVariablesValidationError}
          onFieldChange={() => this.setState({ testVariablesValidationError: "" })}
        />
        {!this.state.showPopup ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "6px",
            }}
          >
            <button
              onClick={this.handleTalkButtonClick}
              disabled={inCall || assistantMissing || this.state.talkAssistantLoading}
              title={
                assistantMissing
                  ? "Assistant ID is not available yet. Save the workspace or wait for deploy to finish."
                  : undefined
              }
              className="btn linear-gradient-cancel yr-cancelbtn mg-r"
              type="button"
              style={{ marginRight: 0 }}
            >
              <span>
                {outbound ? this.props.buttonText || defaultLabel : defaultLabel}
              </span>
            </button>
            {outbound && showOutboundEditLink ? (
              <>
                <button
                  type="button"
                  onClick={this.handleEditVariablesClick}
                  disabled={this.state.editTestVariablesLoading}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    fontSize: "13px",
                    color: "#5867dd",
                    cursor: this.state.editTestVariablesLoading ? "not-allowed" : "pointer",
                    textDecoration: "underline",
                    fontWeight: 500,
                    lineHeight: 1.2,
                    marginLeft: "2px",
                    opacity: this.state.editTestVariablesLoading ? 0.6 : 1,
                  }}
                >
                  Edit Variables
                </button>
                {this.state.editTestVariablesError ? (
                  <Message
                    negative
                    size="small"
                    onDismiss={() => this.setState({ editTestVariablesError: "" })}
                    style={{ marginTop: 4, marginBottom: 0, maxWidth: 320 }}
                  >
                    {this.state.editTestVariablesError}
                  </Message>
                ) : null}
              </>
            ) : null}
          </div>
        ) : (
          <div
            style={{
              position: "fixed",
              bottom: "24px",
              right: "24px",
              zIndex: 1000,
              fontFamily: "Arial, sans-serif",
              pointerEvents: "all",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "20px",
                width: "420px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "1px solid #e1e5e9",
                position: "relative",
              }}
            >
              {this.state.waitingForFirstResponse && !this.state.inCall && (
                <>
                  <div className="ui loading form"></div>
                  <Loader
                    style={{
                      display: "block",
                    }}
                  />
                </>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: this.state.isSpeaking ? "#ff4444" : "#12A594",
                      animation: this.state.isSpeaking ? "pulse 1s infinite" : "none",
                    }}
                  />
                  <span style={{ fontWeight: "bold", color: "#333" }}>
                    {this.state.isSpeaking
                      ? "Assistant Speaking..."
                      : this.state.waitingForFirstResponse
                        ? outbound
                          ? "Speak first…"
                          : "Loading..."
                        : "Listening..."}
                  </span>
                </div>

                <button
                  onClick={this.endCall}
                  disabled={!showPopup}
                  style={{
                    background: "#ff4444",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "6px 12px",
                    fontSize: "12px",
                    cursor: "pointer",
                    fontWeight: "800",
                  }}
                  type="button"
                >
                  <span style={{ color: "#fff" }}>
                    <i className="fa fa-remove" aria-hidden="true" />
                  </span>{" "}
                  End Call
                </button>
              </div>

                <div
                ref={this.transcriptRef}
                style={{
                  minHeight: "500px",
                  maxHeight: "500px",
                  overflowY: "auto",
                  marginBottom: "12px",
                  padding: "8px",
                  background: "#f8f9fa",
                  borderRadius: "8px",
                }}
              >
                {transcript.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      margin: "5px 0",
                      padding: "8px 12px",
                      borderRadius: "12px",
                      display: "inline-block",
                      maxWidth: "80%",
                      background: msg.role === "user" ? "#12A594" : "#333",
                      color: "#fff",
                      float: msg.role === "user" ? "right" : "left",
                      clear: "both",
                    }}
                  >
                    {msg.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default VapiAssistant;
